/**
 * Sidepanel - Wikipedia content display panel
 *
 * Displays structure information and embedded Wikipedia articles
 * in a slide-out panel on the right side of the screen.
 */

/**
 * Sidepanel class - manages structure info display
 */
export class Sidepanel {
  /**
   * Create a new Sidepanel
   * @param {string|HTMLElement} container - Container element or ID
   */
  constructor(container) {
    this.container = typeof container === 'string'
      ? document.getElementById(container)
      : container;

    this.isOpen = false;
    this.currentStructure = null;
    this.iframeLoaded = false;
    this.iframeTimeout = null;

    this.render();
    this.bindEvents();
  }

  /**
   * Render the sidepanel HTML
   * @private
   */
  render() {
    this.element = document.createElement('aside');
    this.element.className = 'sidepanel';
    this.element.setAttribute('aria-hidden', 'true');
    this.element.innerHTML = `
      <div class="sidepanel-header">
        <h2 class="sidepanel-title"></h2>
        <button class="sidepanel-close" aria-label="Schließen">&times;</button>
      </div>
      <div class="sidepanel-content">
        <p class="sidepanel-description"></p>
        <div class="sidepanel-wikipedia">
          <div class="sidepanel-loading">
            <span class="spinner"></span>
            <span>Wikipedia wird geladen...</span>
          </div>
          <iframe
            class="sidepanel-iframe"
            sandbox="allow-scripts allow-same-origin"
            title="Wikipedia Artikel"
          ></iframe>
          <div class="sidepanel-fallback" hidden>
            <p>Der Wikipedia-Artikel kann nicht eingebettet werden.</p>
            <a class="sidepanel-link" href="#" target="_blank" rel="noopener">
              Artikel auf Wikipedia öffnen
            </a>
          </div>
          <div class="sidepanel-no-wiki" hidden>
            <p>Für diese Struktur ist kein Wikipedia-Artikel verfügbar.</p>
          </div>
        </div>
      </div>
    `;

    // Cache DOM references
    this.titleEl = this.element.querySelector('.sidepanel-title');
    this.descriptionEl = this.element.querySelector('.sidepanel-description');
    this.closeBtn = this.element.querySelector('.sidepanel-close');
    this.iframe = this.element.querySelector('.sidepanel-iframe');
    this.loadingEl = this.element.querySelector('.sidepanel-loading');
    this.fallbackEl = this.element.querySelector('.sidepanel-fallback');
    this.fallbackLink = this.element.querySelector('.sidepanel-link');
    this.noWikiEl = this.element.querySelector('.sidepanel-no-wiki');

    this.container.appendChild(this.element);
  }

  /**
   * Bind event listeners
   * @private
   */
  bindEvents() {
    // Close button
    this.closeBtn.addEventListener('click', () => this.close());

    // Escape key
    this._escapeHandler = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    };
    document.addEventListener('keydown', this._escapeHandler);

    // Iframe load handling
    this.iframe.addEventListener('load', () => this.onIframeLoad());
    this.iframe.addEventListener('error', () => this.onIframeError());
  }

  /**
   * Show structure details in the panel
   * @param {Object} structure - Structure data from selection event
   */
  show(structure) {
    this.currentStructure = structure;
    this.iframeLoaded = false;

    // Clear any pending timeout
    if (this.iframeTimeout) {
      clearTimeout(this.iframeTimeout);
    }

    // Update content
    this.titleEl.textContent = structure.name;
    this.descriptionEl.textContent = structure.shortDescription || '';

    // Handle description visibility
    if (structure.shortDescription) {
      this.descriptionEl.hidden = false;
    } else {
      this.descriptionEl.hidden = true;
    }

    // Check if structure has Wikipedia link
    if (structure.wikipediaName) {
      // Reset states
      this.loadingEl.hidden = false;
      this.iframe.hidden = true;
      this.fallbackEl.hidden = true;
      this.noWikiEl.hidden = true;

      // Build Wikipedia URL
      const wikiName = structure.wikipediaName;
      const wikiUrl = `https://de.m.wikipedia.org/wiki/${encodeURIComponent(wikiName)}`;
      const directUrl = `https://de.wikipedia.org/wiki/${encodeURIComponent(wikiName)}`;

      this.iframe.src = wikiUrl;
      this.fallbackLink.href = directUrl;

      // Start timeout for fallback detection
      // Some browsers don't fire error event for X-Frame-Options
      this.iframeTimeout = setTimeout(() => {
        if (!this.iframeLoaded) {
          this.onIframeError();
        }
      }, 5000);
    } else {
      // No Wikipedia article available
      this.loadingEl.hidden = true;
      this.iframe.hidden = true;
      this.fallbackEl.hidden = true;
      this.noWikiEl.hidden = false;
      this.iframe.src = 'about:blank';
    }

    // Open panel
    this.open();
  }

  /**
   * Handle successful iframe load
   * @private
   */
  onIframeLoad() {
    // Only process if we haven't already handled this
    if (this.iframeLoaded) {
      return;
    }

    this.iframeLoaded = true;
    if (this.iframeTimeout) {
      clearTimeout(this.iframeTimeout);
    }

    // Check if the iframe actually loaded content
    // (src='about:blank' also fires load event)
    if (this.iframe.src && this.iframe.src !== 'about:blank') {
      this.loadingEl.hidden = true;
      this.iframe.hidden = false;
      this.fallbackEl.hidden = true;
    }
  }

  /**
   * Handle iframe load failure
   * @private
   */
  onIframeError() {
    if (this.iframeTimeout) {
      clearTimeout(this.iframeTimeout);
    }

    this.loadingEl.hidden = true;
    this.iframe.hidden = true;
    this.fallbackEl.hidden = false;
  }

  /**
   * Open the panel
   */
  open() {
    this.isOpen = true;
    this.element.classList.add('sidepanel--open');
    this.element.setAttribute('aria-hidden', 'false');
  }

  /**
   * Close the panel
   */
  close() {
    this.isOpen = false;
    this.element.classList.remove('sidepanel--open');
    this.element.setAttribute('aria-hidden', 'true');

    // Clear iframe to stop any loading
    this.iframe.src = 'about:blank';
    if (this.iframeTimeout) {
      clearTimeout(this.iframeTimeout);
    }
  }

  /**
   * Toggle panel visibility
   */
  toggle() {
    if (this.isOpen) {
      this.close();
    } else if (this.currentStructure) {
      this.open();
    }
  }

  /**
   * Check if panel is open
   * @returns {boolean}
   */
  isVisible() {
    return this.isOpen;
  }

  /**
   * Get current structure
   * @returns {Object|null}
   */
  getCurrentStructure() {
    return this.currentStructure;
  }

  /**
   * Destroy the sidepanel and clean up
   */
  destroy() {
    document.removeEventListener('keydown', this._escapeHandler);
    if (this.iframeTimeout) {
      clearTimeout(this.iframeTimeout);
    }
    this.element.remove();
  }
}
